import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryProvider } from './lib/providers/QueryProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmationProvider } from './components/ui/ConfirmationDialog';
import { LoginForm } from './components/auth/LoginForm';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { Button } from './components/ui/Button';
import { X } from 'lucide-react';
import { Layout } from './components/layout/Layout';
import { ProfileCompletionWrapper } from './components/auth/ProfileCompletionWrapper';
import AdminDashboard from './components/dashboards/AdminDashboard';
import UserProfilePage from './components/auth/UserProfilePage';
import Support from './pages/Support';
import SGAReportsPage from './components/reports/SGAReportsPage';
import CallAuditReportsPage from './components/reports/CallAuditReportsPage';
import CallAuditParamsUploadPage from './components/reports/CallAuditParamsUploadPage';
import RecruiterDashboard from './components/dashboards/RecruiterDashboard';
import TeamLeadDashboard from './components/dashboards/TeamLeadDashboard';
import OpsHeadDashboard from './components/dashboards/OpsHeadDashboard';
import CEODashboard from './components/dashboards/CEODashboard';
import MisCoordinatorDashboard from './components/dashboards/MisCoordinatorDashboard';
import Requirements from './components/requirements/Requirements';
import CreateRequirementPage from './components/requirements/CreateRequirementPage';
import EditRequirementPage from './components/requirements/EditRequirementPage';
import RequirementCandidatesPage from './components/requirements/RequirementCandidatesPage';
import RequirementDetailsPage from './pages/RequirementDetailsPage';
import { CallScriptPage } from './pages/CallScriptPage';
import CandidateSubmissionsPage from './pages/CandidateSubmissionsPage';
import { RequirementsValueManagement } from './components/admin/RequirementsValueManagement';
import UserManagement from './components/admin/UserManagement';
import { Clients } from './pages/Clients';
import Candidates from './pages/Candidates';
import CandidateDetails from './pages/CandidateDetails';
import { CandidateSubmissionPage } from './components/candidates/CandidateSubmissionPage';
import CandidateAnalysisPage from './components/candidates/CandidateAnalysisPage';
import AnalysisResultsPage from './components/candidates/AnalysisResultsPage';
import { JDCVAnalysisPage } from './components/candidates/JDCVAnalysisPage';
import { EnhancedJDCVAnalysisPage } from './components/candidates/EnhancedJDCVAnalysisPage';
import SGAReportPage from './pages/SGAReportPage';
import CallAuditReportPage from './pages/CallAuditReportPage';
import WorkforceInsights from './pages/WorkforceInsights';
import Interviews from './pages/Interviews';
import ThemeAppearance from './pages/ThemeAppearance';
import AdminRoleAccess from './pages/AdminRoleAccess';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { CandidateCallHistoryPage } from './pages/CandidateCallHistoryPage';
import { MergedTranscriptionPage } from './pages/MergedTranscriptionPage';
import { ConsolidatedAuditReportPage } from './pages/ConsolidatedAuditReportPage';
import { TeamLeadCallRecordingsPage } from './pages/TeamLeadCallRecordingsPage';
 

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[]; allowedScopes?: string[] }> = ({ 
  children, 
  allowedRoles,
  allowedScopes
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();
  
  console.log('🛡️ ProtectedRoute: isAuthenticated:', isAuthenticated, 'user:', user, 'currentPath:', location.pathname);

  // Redirect loop detection
  const [redirectCount, setRedirectCount] = React.useState(0);
  const [lastRedirectPath, setLastRedirectPath] = React.useState<string | null>(null);

  React.useEffect(() => {
    const currentPath = location.pathname;
    if (lastRedirectPath === currentPath) {
      setRedirectCount(prev => prev + 1);
    } else {
      setRedirectCount(0);
    }
    setLastRedirectPath(currentPath);
  }, [location.pathname, lastRedirectPath]);

  // If we've redirected to the same path multiple times, show error page
  if (redirectCount >= 3) {
    console.log('🔄 Redirect loop detected, showing error page');
    return <Navigate to="/error" replace />;
  }

  // Wait for authentication state to load
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414]">
        <LoadingSpinner size="lg" variant="ai" showText text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('🛡️ ProtectedRoute: Not authenticated, redirecting to /login');
    // Persist intended path for post-login redirect
    try {
      const intended = location.pathname + location.search;
      sessionStorage.setItem('post_login_redirect', intended);
    } catch (e) {
      console.warn('Failed to persist post-login redirect path', e);
    }
    return <Navigate to="/login" replace />;
  }

  // Check if user has a specific scope OR has all:admin (which grants everything)
  const hasScope = (scope: string) => Boolean(user?.scopes?.includes(scope) || user?.scopes?.includes('all:admin'));
  const hasAnyAllowedScope = Array.isArray(allowedScopes) && allowedScopes.length > 0 && allowedScopes.some(hasScope);

  // Debug logging
  console.log('🛡️ ProtectedRoute Debug:', {
    userRole: user?.role,
    userScopes: user?.scopes,
    allowedRoles,
    allowedScopes,
    hasAnyAllowedScope,
    currentPath: location.pathname
  });

  // Check role/scope permissions only if profile is complete
  // If profile is incomplete, let ProfileCompletionWrapper handle it
  if (user && (
    (Array.isArray(allowedRoles) && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) ||
    (Array.isArray(allowedScopes) && allowedScopes.length > 0 && !hasAnyAllowedScope)
  )) {
    // Special case: All roles except recruiters should have access to all routes regardless of specific scopes
    if (user.role !== 'recruiter') {
      console.log('🛡️ ProtectedRoute: Non-recruiter user, allowing access regardless of specific scopes');
      return (
        <ProfileCompletionWrapper>
          <Layout>{children}</Layout>
        </ProfileCompletionWrapper>
      );
    }
    // Special case: If user has the right role but missing scopes, allow access for basic pages
    const hasRightRole = Array.isArray(allowedRoles) && allowedRoles.includes(user.role);
    const isBasicPage = location.pathname === '/requirements' || location.pathname === '/candidates';
    
    if (hasRightRole && isBasicPage) {
      console.log('🛡️ ProtectedRoute: User has right role for basic page, allowing access');
      return (
        <ProfileCompletionWrapper>
          <Layout>{children}</Layout>
        </ProfileCompletionWrapper>
      );
    }
    console.log('🛡️ ProtectedRoute: User role/scope not allowed, redirecting to appropriate dashboard');
    // Redirect to appropriate dashboard based on user role
    const roleRoutes: Record<string, string> = {
      'recruiter': '/recruiter',
      'team_lead': '/team-lead',
      'ops_head': '/ops-head',
      'ceo': '/ceo',
      'admin': '/admin',
      'mis_coordinator': '/mis-cordinator',
      'manager': '/recruiter'
    };
    
    const redirectPath = roleRoutes[user.role] || '/recruiter';
    console.log('🛡️ ProtectedRoute: Redirecting to:', redirectPath);
    return <Navigate to={redirectPath} replace />;
  }

  console.log('🛡️ ProtectedRoute: Access granted, rendering children');
  return (
    <ProfileCompletionWrapper>
      <Layout>{children}</Layout>
    </ProfileCompletionWrapper>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <QueryProvider>
      <ThemeProvider>
        <LoadingProvider>
          <AuthProvider>
            <ToastProvider>
              <ConfirmationProvider>
              <Router>
              <div className="min-h-screen bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414]">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<AuthenticatedRedirect />} />
              <Route path="/login/" element={<AuthenticatedRedirect />} />
              <Route path="/password/forgot" element={<ForgotPassword />} />
              <Route path="/password/reset" element={<ResetPassword />} />
              <Route path="/error" element={<ErrorFallback />} />
              
              {/* Protected Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute allowedRoles={['admin', 'ceo']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/recruiter" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'manager']}>
                    <RecruiterDashboard />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/team-lead" 
                element={
                  <ProtectedRoute allowedRoles={['team_lead', 'ops_head', 'ceo']}>
                    <TeamLeadDashboard />
                  </ProtectedRoute>
                } 
              />

              {/* Profile Page */}
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <UserProfilePage />
                  </ProtectedRoute>
                } 
              />

              {/* Support Page - Available for all users */}
              <Route 
                path="/support" 
                element={
                  <ProtectedRoute>
                    <Support />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/settings/appearance"
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'hr_coordinator', 'manager', 'viewer']}>
                    <ThemeAppearance />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/ops-head" 
                element={
                  <ProtectedRoute allowedRoles={['ops_head', 'ceo']}>
                    <OpsHeadDashboard />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/ceo" 
                element={
                  <ProtectedRoute allowedRoles={['ceo']}>
                    <CEODashboard />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/mis-cordinator" 
                element={
                  <ProtectedRoute allowedRoles={['mis_coordinator','admin','ceo','ops_head']} allowedScopes={['user_management:access','dashboard:view']}>
                    <MisCoordinatorDashboard />
                  </ProtectedRoute>
                } 
              />
              
              
                            {/* Requirements Route */}
              <Route 
                path="/requirements" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['requirements:view']}>
                    <Requirements />
                  </ProtectedRoute>
                } 
              />

              {/* Create Requirement Route */}
              <Route 
                path="/requirements/create" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['requirements:add']}>
                    <CreateRequirementPage />
                  </ProtectedRoute>
                } 
              />

              {/* Edit Requirement Route */}
              <Route 
                path="/requirements/:id/edit" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['requirements:edit']}>
                    <EditRequirementPage />
                  </ProtectedRoute>
                } 
              />

              {/* Interviews Route (visible to HR Coordinator and Team Lead for testing) */}
              <Route 
                path="/interviews" 
                element={
                  <ProtectedRoute allowedRoles={['hr_coordinator', 'team_lead', 'recruiter', 'ops_head', 'ceo', 'admin']} allowedScopes={['candidates:view']}>
                    <Interviews />
                  </ProtectedRoute>
                } 
              />

              {/* Reports */}
              <Route 
                path="/reports/sga/:candidateId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['candidates:view']}>
                    <SGAReportsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/reports/audit/:recruiterId" 
                element={
                  <ProtectedRoute allowedRoles={['team_lead', 'ops_head', 'ceo', 'admin', 'recruiter']} allowedScopes={['candidates:view']}>
                    <CallAuditReportsPage />
                  </ProtectedRoute>
                } 
              />

              {/* Team Lead: Upload Call Audit Params */}
              <Route 
                path="/team-lead/audit-params" 
                element={
                  <ProtectedRoute allowedRoles={['team_lead', 'ops_head', 'ceo', 'admin']} allowedScopes={['audit_params:view']}>
                    <CallAuditParamsUploadPage />
                  </ProtectedRoute>
                } 
              />

              {/* Call Recordings - For Team Lead, Ops Head, CEO, Admin */}
              <Route 
                path="/call-recordings" 
                element={
                  <ProtectedRoute allowedRoles={['team_lead', 'ops_head', 'ceo', 'admin']}>
                    <TeamLeadCallRecordingsPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Requirement Details Route */}
              <Route 
                path="/requirements/:id/view" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['requirements:view']}>
                    <RequirementDetailsPage />
                  </ProtectedRoute>
                } 
              />

              {/* Requirement Candidates Route */}
        <Route
          path="/requirements/:requirementId/candidates"
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['requirements:view','candidates:view']}>
              <RequirementCandidatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/candidates/:candidateId/submissions"
          element={
            <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
              <CandidateSubmissionsPage />
            </ProtectedRoute>
          }
        />
              
              {/* Candidates Route */}
              <Route 
                path="/candidates" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['candidates:view']}>
                    <Candidates />
                  </ProtectedRoute>
                } 
              />

              
              
              {/* Candidate Details Route */}
              <Route 
                path="/candidates/:candidateId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <CandidateDetails />
                  </ProtectedRoute>
                } 
              />
              
              {/* Candidate Submission Route */}
              <Route 
                path="/candidates/submit/:requirementId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['candidates:add','requirements:view']}>
                    <CandidateSubmissionPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Candidate Analysis Route - Now Enhanced by Default */}
              <Route 
                path="/candidates/:candidateId/analysis/:requirementId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['candidates:view','requirements:view']}>
                    <EnhancedJDCVAnalysisPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Legacy JD vs CV Analysis Route */}
              <Route 
                path="/candidates/:candidateId/jd-cv-analysis/:requirementId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['candidates:view','requirements:view']}>
                    <JDCVAnalysisPage />
                  </ProtectedRoute>
                } 
              />

              {/* Call Script Route */}
              <Route 
                path="/calls/script/:sessionId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <CallScriptPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Legacy Original Analysis Route */}
              <Route 
                path="/candidates/:candidateId/original-analysis/:requirementId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo', 'admin', 'manager']} allowedScopes={['candidates:view','requirements:view']}>
                    <CandidateAnalysisPage />
                  </ProtectedRoute>
                }
              />

              {/* SGA Report Route */}
              <Route 
                path="/candidates/:candidateId/sga-report" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <SGAReportPage />
                  </ProtectedRoute>
                }
              />

              {/* Call Audit Report Route */}
              <Route 
                path="/candidates/:candidateId/call-audit" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <CallAuditReportPage />
                  </ProtectedRoute>
                }
              />

              {/* Call History & Consolidation Routes */}
              <Route 
                path="/candidates/:candidateId/calls" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <CandidateCallHistoryPage />
                  </ProtectedRoute>
                }
              />

              {/* Individual transcription route */}
              <Route 
                path="/transcriptions/:sessionId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <MergedTranscriptionPage />
                  </ProtectedRoute>
                }
              />

              {/* Merged transcription route (more specific, must come after) */}
              <Route 
                path="/transcriptions/merged/:sessionId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <MergedTranscriptionPage />
                  </ProtectedRoute>
                }
              />

              <Route 
                path="/audit-reports/consolidated/:sessionId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <ConsolidatedAuditReportPage />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/analysis/:analysisId" 
                element={
                  <ProtectedRoute allowedRoles={['recruiter', 'team_lead', 'ops_head', 'ceo']} allowedScopes={['candidates:view']}>
                    <AnalysisResultsPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Clients Route */}
              <Route 
                path="/clients" 
                element={
                  <ProtectedRoute allowedRoles={['admin', 'ops_head', 'ceo']} allowedScopes={['clients:view']}>
                    <Clients />
                  </ProtectedRoute>
                } 
              />

              {/* Workforce Insights (testing: allow all authenticated roles) */}
              <Route 
                path="/workforce-insights" 
                element={
                  <ProtectedRoute>
                    <WorkforceInsights />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin/users" 
                element={
                  <ProtectedRoute allowedRoles={['admin', 'ceo', 'ops_head', 'mis_coordinator']} allowedScopes={['user_management:access']}>
                    <UserManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/requirement-values" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <RequirementsValueManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/roles" 
                element={
                  <ProtectedRoute allowedRoles={['admin','ceo']}>
                    <AdminRoleAccess />
                  </ProtectedRoute>
                }
              />
              
              {/* Default redirect based on user role */}
              <Route 
                path="/" 
                element={<DefaultRedirect />} 
              />
              
              {/* Error Fallback Route */}
              <Route path="/error" element={<ErrorFallback />} />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </div>
              </Router>
            </ConfirmationProvider>
          </ToastProvider>
        </AuthProvider>
      </LoadingProvider>
      </ThemeProvider>
    </QueryProvider>
  );
};

// Authenticated Redirect Component - Redirects authenticated users away from login
const AuthenticatedRedirect: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Wait for authentication state to load
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414]">
        <LoadingSpinner size="lg" variant="ai" showText text="Loading..." />
      </div>
    );
  }

  // If user is already authenticated, redirect to their dashboard
  if (isAuthenticated && user) {
    // If a post-login redirect is set, honor it first
    try {
      const storedRedirect = sessionStorage.getItem('post_login_redirect');
      if (storedRedirect && typeof storedRedirect === 'string') {
        sessionStorage.removeItem('post_login_redirect');
        console.log('🔄 AuthenticatedRedirect: Using stored post-login redirect:', storedRedirect);
        return <Navigate to={storedRedirect} replace />;
      }
    } catch (e) {
      console.warn('Failed to read/clear post-login redirect path', e);
    }
    const roleRoutes: Record<string, string> = {
      'recruiter': '/recruiter',
      'team_lead': '/team-lead',
      'ops_head': '/ops-head',
      'ceo': '/ceo',
      'admin': '/admin',
      'mis_coordinator': '/mis-cordinator'
    };
    
    const redirectPath = roleRoutes[user.role] || '/recruiter';
    console.log('🔄 AuthenticatedRedirect: User already logged in, redirecting to:', redirectPath);
    return <Navigate to={redirectPath} replace />;
  }

  // User is not authenticated, show login form
  return <LoginForm />;
};

// Default Redirect Component - Only redirects from root path
const DefaultRedirect: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Wait for authentication state to load
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background-200 dark:from-[#141414] via-background-100 dark:via-[#1A1A1A] to-background-200 dark:to-[#141414]">
        <LoadingSpinner size="lg" variant="ai" showText text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only redirect from root path to appropriate dashboard based on user role
  const roleRoutes: Record<string, string> = {
    'recruiter': '/recruiter',
    'team_lead': '/team-lead',
    'ops_head': '/ops-head',
    'ceo': '/ceo',
    'admin': '/admin',
    'mis_coordinator': '/mis-cordinator'
  };
  
  const redirectPath = roleRoutes[user?.role || ''] || '/recruiter';
  console.log('🔄 DefaultRedirect: Redirecting authenticated user from root to:', redirectPath);
  return <Navigate to={redirectPath} replace />;
};

// Error Fallback Component - Shows when there are routing issues
const ErrorFallback: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Force redirect to login even if logout fails
      window.location.href = '/login';
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background-200 dark:from-background-dark-200 via-background-100 dark:via-background-dark-100 to-background-200 dark:to-background-dark-200">
      <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-error-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-8 h-8 text-error-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-text-enterprise-primary mb-4">
            Something went wrong
          </h1>
          
          <p className="text-text-enterprise-secondary mb-6">
            We encountered an issue with your session. This might be due to incomplete profile setup or permission issues.
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={handleRetry}
              variant="default"
              className="w-full"
            >
              Try Again
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full"
            >
              Logout & Sign In Again
            </Button>
          </div>
          
          <p className="text-xs text-text-enterprise-tertiary mt-4">
            If the problem persists, please contact support.
          </p>
        </div>
      </div>
    </div>
  );
};

export default App;

// Force cache bust: 20251212113521
